import React, { ComponentProps, ReactElement } from 'react';

import { twMerge } from 'tailwind-merge';

import { Button } from '../../Button';

import { BaseInput } from './BaseInput';

interface InputGroupProps<T> {
  readonly orientation?: 'horizontal' | 'vertical';
  readonly children: T;
  readonly className?: string;
}

function InputGroup<
  T extends Array<React.ReactElement<ComponentProps<typeof BaseInput>> | React.ReactElement<ComponentProps<typeof Button>>>,
>({ children, orientation = 'horizontal', className }: InputGroupProps<T>) {
  const classes = twMerge('flex', orientation === 'horizontal' ? 'flex-row' : 'flex-col', className);
  const total = React.Children.count(children);

  return (
    <div className={classes}>
      {React.Children.map(children, (child, index) => {
        let rounding: (typeof child)['props']['rounding'];
        let border = '';

        if (orientation === 'horizontal') {
          switch (index) {
            case 0:
              rounding = 'left';
              border = 'border-r-0';
              break;
            case total - 1:
              rounding = 'right';
              break;
            default:
              rounding = 'none';
              border = 'border-r-0';
          }
        } else {
          switch (index) {
            case 0:
              rounding = 'top';
              border = 'border-b-0';
              break;
            case total - 1:
              rounding = 'bottom';
              border = 'default';
              break;
            default:
              rounding = 'none';
              border = 'border-b-0';
          }
        }

        return React.cloneElement(child as ReactElement, {
          rounding,
          className: twMerge(child.props.className, border, 'focus-within:ring-inset focus:ring-neutral-300/50'),
        });
      })}
    </div>
  );
}

export { InputGroup };
